import { AlertResolution, ALERT_RESOLUTION_LABEL } from '@minisoc/contracts';
import {
  AlertStatusBadge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  FieldValue,
  JsonBlock,
  PageHeader,
  ReactionPlanCard,
  SeverityBadge,
  Spinner,
  formatDate,
  formatDateTime,
  formatTime,
} from '@minisoc/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { AssistantPanel } from '../components/AssistantPanel';

export function AlertDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [closing, setClosing] = useState<AlertResolution | null>(null);
  const [note, setNote] = useState('');

  const alert = useQuery({ queryKey: ['alerts', 'detail', id], queryFn: () => api.getAlert(id) });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    void queryClient.invalidateQueries({ queryKey: ['kpi'] });
  };

  const toggleStep = useMutation({
    mutationFn: ({ order, done }: { order: number; done: boolean }) =>
      api.toggleReactionStep(id, order, done),
    onSuccess: invalidate,
  });

  const acknowledge = useMutation({
    mutationFn: () => api.acknowledgeAlert(id),
    onSuccess: invalidate,
  });

  const close = useMutation({
    mutationFn: (resolution: AlertResolution) =>
      api.closeAlert(id, {
        resolution,
        note: note.trim() || undefined,
        completedSteps: alert.data?.reactionPlan.steps.filter((s) => s.completed).map((s) => s.order),
      }),
    onSuccess: () => {
      setClosing(null);
      setNote('');
      invalidate();
    },
  });

  if (alert.isPending) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-text-muted">
        <Spinner />
        Chargement de l'alerte…
      </div>
    );
  }

  if (alert.isError || !alert.data) {
    return (
      <ErrorState
        message="Cette alerte est introuvable ou ne fait pas partie de votre périmètre."
        onRetry={() => navigate('/alertes')}
      />
    );
  }

  const data = alert.data;
  const resolved = data.status === 'RESOLVED';

  return (
    <>
      <PageHeader
        title={data.ruleNameFr}
        subtitle={`Détail de l'alerte ${data.ruleCode}`}
        back={{ label: 'Retour aux alertes', onClick: () => navigate('/alertes') }}
      />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="min-w-0 space-y-6">
          <Card className="p-5">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <SeverityBadge severity={data.severity} />
              <AlertStatusBadge status={data.status} resolution={data.resolution} />
              <span className="font-mono text-sm text-brand-400">{data.ruleCode}</span>
            </div>

            <h2 className="text-xl font-bold text-text">{data.ruleNameFr}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-muted">
              {data.clientExplanation}
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <FieldValue label="Machine" value={data.endpointName} />
              <FieldValue label="Utilisateur" value={data.subjectUser} />
              <FieldValue label="Date" value={formatDate(data.detectedAt)} />
              <FieldValue label="Heure" value={formatTime(data.detectedAt)} />
              <FieldValue label="Event ID" value={data.windowsEventId} />
              <FieldValue label="Source" value={data.logSource} />
              {data.sourceIp ? <FieldValue label="IP source" value={data.sourceIp} /> : null}
              {data.appliance ? <FieldValue label="Nœud" value={data.appliance.code} /> : null}
            </div>

            {data.mitre.length > 0 ? (
              <div className="mt-5">
                <p className="label-caps">Techniques MITRE ATT&amp;CK</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.mitre.map((entry) => (
                    <a
                      key={`${entry.tacticId}-${entry.techniqueId}`}
                      href={entry.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="rounded-lg bg-surface-sunken px-2.5 py-1.5 text-xs text-text-muted ring-1 ring-border-subtle transition-colors hover:text-brand-400 hover:ring-brand-500/30"
                    >
                      <span className="font-mono text-brand-400">{entry.techniqueId}</span>{' '}
                      {entry.techniqueName} · {entry.tacticName}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>

          <Card>
            <CardHeader title="Chronologie" />
            <ol className="divide-y divide-border-subtle/60">
              {data.timeline.map((entry) => (
                <li key={entry.id} className="flex items-baseline gap-4 px-5 py-3">
                  <span className="w-32 shrink-0 font-mono text-xs text-text-dim">
                    {formatDateTime(entry.at)}
                  </span>
                  <span className="min-w-0 flex-1 text-sm text-text">
                    {entry.label}
                    {entry.detail ? (
                      <span className="mt-0.5 block text-xs text-text-muted">{entry.detail}</span>
                    ) : null}
                  </span>
                  {entry.actor ? (
                    <span className="shrink-0 font-mono text-xs text-text-dim">{entry.actor}</span>
                  ) : null}
                </li>
              ))}
            </ol>
          </Card>

          <Card>
            <CardHeader
              title="Log brut Wazuh"
              subtitle="Événement ayant déclenché la règle. L'historique complet reste sur votre nœud."
            />
            <div className="p-4">
              <JsonBlock value={data.rawLog} />
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <ReactionPlanCard
            plan={data.reactionPlan}
            disabled={resolved || toggleStep.isPending}
            onToggleStep={(order, done) => toggleStep.mutate({ order, done })}
            subtitle={
              data.reactionPlan.customized
                ? 'Adapté à votre organisation par MiniSOC'
                : 'Procédure de référence MiniSOC'
            }
            emptyMessage="Aucun plan de réaction n'est défini pour cette règle. Contactez MiniSOC pour une levée de doute assistée."
          />

          {!assistantOpen ? (
            <Button variant="gradient" block onClick={() => setAssistantOpen(true)}>
              🤖 Analyser avec l'assistant IA
            </Button>
          ) : (
            <AssistantPanel alertId={id} />
          )}

          {resolved ? (
            <Card className="p-4">
              <p className="label-caps">Clôture</p>
              <p className="mt-1.5 text-sm text-text">
                {data.resolution ? ALERT_RESOLUTION_LABEL[data.resolution] : '—'}
                {data.resolvedAt ? (
                  <span className="text-text-muted"> le {formatDateTime(data.resolvedAt)}</span>
                ) : null}
              </p>
              {data.resolvedBy ? (
                <p className="mt-1 text-xs text-text-dim">Par {data.resolvedBy}</p>
              ) : null}
              {data.resolutionNote ? (
                <p className="mt-3 rounded-lg bg-surface-sunken px-3 py-2 text-sm text-text-muted">
                  {data.resolutionNote}
                </p>
              ) : null}
            </Card>
          ) : closing ? (
            <Card className="space-y-3 p-4">
              <p className="text-sm font-medium text-text">
                Clôturer en {ALERT_RESOLUTION_LABEL[closing].toLowerCase()}
              </p>

              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={4}
                placeholder={
                  closing === AlertResolution.TRUE_POSITIVE
                    ? 'Obligatoire : ce qui a été constaté et les actions menées.'
                    : 'Optionnel : ce qui explique le déclenchement.'
                }
                className="w-full rounded-lg border border-border-strong bg-surface-sunken px-3 py-2 text-sm text-text outline-none placeholder:text-text-dim focus:border-brand-500"
              />

              {close.isError ? (
                <p role="alert" className="text-sm text-critical">
                  {closing === AlertResolution.TRUE_POSITIVE && !note.trim()
                    ? 'Un commentaire est obligatoire pour clôturer un vrai positif.'
                    : 'La clôture a échoué, réessayez.'}
                </p>
              ) : null}

              <div className="flex gap-2">
                <Button
                  variant={closing === AlertResolution.TRUE_POSITIVE ? 'danger' : 'success'}
                  size="sm"
                  disabled={close.isPending}
                  onClick={() => close.mutate(closing)}
                >
                  Confirmer la clôture
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setClosing(null)}>
                  Annuler
                </Button>
              </div>
            </Card>
          ) : (
            <div className="space-y-2">
              {data.status === 'OPEN' ? (
                <Button block disabled={acknowledge.isPending} onClick={() => acknowledge.mutate()}>
                  Prendre en charge
                </Button>
              ) : null}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="success"
                  onClick={() => setClosing(AlertResolution.FALSE_POSITIVE)}
                >
                  ✓ Faux positif
                </Button>
                <Button variant="danger" onClick={() => setClosing(AlertResolution.TRUE_POSITIVE)}>
                  ⚠ Vrai positif
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
